import { EMPTY_INPUT, TICK_RATE, WORLD_H, WORLD_W, type InputState, type LevelDef, type PlayerState, type Rect, type WorldState } from '../../shared/types';
import { PHYSICS, getPlatforms, isGateOpen, predictMovementWorld } from '../../shared/simulation';
import { RenderClock, TICK_MS, type SnapshotClock } from './timeline';
import { clamp } from './paint';

const STEP = 1 / TICK_RATE;
/** The engine consumes at most 250 ms / 15 fixed ticks of history. */
const MAX_TICKS = 15;
const MAX_MS = MAX_TICKS * TICK_MS;
/** Acknowledgement samples this old no longer describe the transport. */
const LEAD_WINDOW_MS = 2500;
/** Without a calibrated transition yet, the window minimum of acknowledgement leads is biased
 * upward by part of the (phase-locked) wait for the service's next step; a quarter tick is taken off. */
const LEAD_FALLBACK_MARGIN_TICKS = 0.25;
/** Exact application-tick constraints from the courier's own transitions are kept this long. */
const CALIBRATION_WINDOW_MS = 12000;
const CALIBRATION_LIMIT = 10;
const WALK_STEP = PHYSICS.speed / TICK_RATE;
/** A sequence that reads as a recent wall-clock stamp (ms × 100) dates the transmission itself. */
const STAMP_TOLERANCE_MS = 1500;
const CACHE_LIMIT = 12;
const EPS = 1e-9;
type Command = { input: InputState; duration: number };
interface InputRecord {
  input: InputState;
  /** When the buttons/sequence were observed (event time when available); decides the applied tick. */
  at: number;
  /** Best estimate of the transmission time (a validated sequence stamp, else the previous observation as a lower bound); lead samples only. */
  sentAt: number;
  sequence: number | null;
  changed: boolean;
  /** The buttons held before this change (transitions only). */
  before: InputState | null;
  /** Fixed once assigned: the first predicted tick that samples this record. */
  tick: number | null;
  measured: boolean;
}
/** ceil(clockTick(sentAt) + lead) must equal the tick the service applied a transition at: lead ∈ (lo, hi]. */
interface Calibration { at: number; lo: number; hi: number; tick: number }
/** Two consecutive predicted ticks and the fraction between them. */
export interface TrajectorySample { lo: WorldState; hi: WorldState; frac: number; ticks: number; tick: number }
const sameButtons = (a: InputState, b: InputState) => a.left === b.left && a.right === b.right && a.jump === b.jump;
const active = (p: PlayerState) => p.connected && !p.entered;
const code = (input: InputState) => `${input.left ? 'L' : ''}${input.right ? 'R' : ''}${input.jump ? 'J' : ''},`;
/** The network hook stamps sequences from the wall clock (ms × 100). When a sequence reads as a
 * time within tolerance of now it dates the transmission exactly; any other numbering falls back
 * to the observation bound, so this is a timing hint and never a correctness dependency. */
function transmissionTime(sequence: number, now: number): number | null {
  if (typeof performance === 'undefined' || !Number.isFinite(performance.timeOrigin)) return null;
  const stamped = sequence / 100 - performance.timeOrigin;
  return Math.abs(stamped - now) <= STAMP_TOLERANCE_MS ? Math.min(stamped, now) : null;
}

/**
 * The exact tick at which the service applied a transition, read from the authoritative
 * motion of a FREE courier across the acknowledging snapshot (previous tick → ack tick, one to
 * four ticks apart). Walking moves an exact 220/60 px per tick and a jump's first ticks descend a
 * known ballistic table, so the count of moved/flown ticks dates the application. Anything not
 * matching a clean pattern (blocked, carried, pushed, mid-air) yields no sample.
 */
function appliedTick(record: InputRecord, previous: WorldState, world: WorldState, localId: string): number | null {
  const before = previous.players.find(p => p.id === localId);
  const after = world.players.find(p => p.id === localId);
  const beforeTick = previous.tick, afterTick = world.tick;
  const gap = afterTick - beforeTick;
  if (!before || !after || gap < 1 || gap > 4 || !record.before) return null;
  // A courier standing on another body or a crate inherits its motion: no clean sample.
  const carried = (p: PlayerState, w: WorldState) => !!p.supportId && (w.players.some(q => q.id === p.supportId) || w.crates.some(c => c.id === p.supportId));
  if (carried(before, previous) || carried(after, world)) return null;
  const dx = after.x - before.x;
  const dy = after.y - before.y;
  const moved = Math.abs(dx) / WALK_STEP;
  const wholeSteps = Math.abs(moved - Math.round(moved)) < 0.02 ? Math.round(moved) : null;
  const wasDir = Number(record.before.right) - Number(record.before.left);
  const isDir = Number(record.input.right) - Number(record.input.left);
  if (record.input.jump && !record.before.jump && before.grounded) {
    // Flight ticks by cumulative descent: vy starts at -jump, gravity applies before each move.
    let y = 0, vy = -PHYSICS.jump;
    for (let flight = 1; flight <= gap; flight++) {
      vy = Math.min(PHYSICS.maxFall, vy + PHYSICS.gravity / TICK_RATE);
      y += vy / TICK_RATE;
      if (Math.abs(dy - y) < 0.02) return afterTick - flight + 1;
    }
    return null;
  }
  if (record.input.jump !== record.before.jump) return null;
  if (!before.grounded || !after.grounded || Math.abs(dy) > 0.02 || wholeSteps === null) return null;
  if (wasDir === 0 && isDir !== 0) {
    // Start: the moved ticks are the last ones of the window.
    if (wholeSteps < 1 || wholeSteps > gap || Math.sign(dx) !== isDir) return null;
    return afterTick - wholeSteps + 1;
  }
  if (wasDir !== 0 && isDir === 0) {
    // Stop: the moved ticks are the first ones of the window.
    if (wholeSteps > gap - 1 || (wholeSteps > 0 && Math.sign(dx) !== wasDir)) return null;
    return beforeTick + 1 + wholeSteps;
  }
  if (wasDir !== 0 && isDir === -wasDir) {
    // Reversal: m ticks the old way, gap - m the new way → dx = (gap - 2m) steps the new way.
    const signed = dx / WALK_STEP * isDir;
    const m = (gap - signed) / 2;
    if (Math.abs(m - Math.round(m)) > 0.02 || m < 0 || m > gap - 1) return null;
    return beforeTick + 1 + Math.round(m);
  }
  return null;
}

/** Side contact of two bodies at the authority (the solver leaves exact faces; this absorbs roundoff). */
const CHAIN_CONTACT = 1e-3;
/** Inferred intents this far (px/s) from a held-direction value are not trusted. */
const INTENT_SNAP = PHYSICS.speed * 0.25;
const overlapY = (a: Rect, b: Rect) => Math.min(a.y + a.h, b.y + b.h) - Math.max(a.y, b.y);
const touchesRight = (a: Rect, b: Rect) => Math.abs(a.x + a.w - b.x) <= CHAIN_CONTACT && overlapY(a, b) > EPS;

export interface ChainInference { members: number; adjusted: number; own: number | null; reason: 'none' | 'carried' | 'wall' | 'shared' | 'ambiguous' }

/**
 * The world handed to the engine's replay. The engine reads each remote courier's held
 * direction from its snapshot `vx`, which is the RESOLVED velocity: a courier being pushed
 * reports the shared push speed and would be replayed as walking away at full speed, and a
 * courier blocked head-on reports zero and would be replayed as idle. Along the local
 * courier's own side-contact chain both are decidable from the acknowledged local input:
 * the horizontal solver shares displacement between couriers, so Σ resolved = Σ own while the
 * chain has room, and a chain stopped by a wall has idle members. The remotes' `vx` is
 * replaced by that inferred own velocity (their riders keep their relative velocity, which is
 * what the engine reads for them). The authority itself is never modified; only the replay's
 * starting velocities are, and every replayed velocity comes back resolved.
 */
export function inferChainIntent(world: WorldState, level: LevelDef, localId: string, held: InputState): { world: WorldState; inference: ChainInference } {
  const none = (reason: ChainInference['reason'], members = 1): { world: WorldState; inference: ChainInference } => ({ world, inference: { members, adjusted: 0, own: null, reason } });
  const local = world.players.find(p => p.id === localId);
  if (!local || !active(local)) return none('none');
  const players = world.players.filter(active);
  const chain: PlayerState[] = [local];
  for (let changed = true; changed;) {
    changed = false;
    for (const p of players) {
      if (chain.includes(p)) continue;
      if (chain.some(m => touchesRight(m, p) || touchesRight(p, m))) { chain.push(p); changed = true; }
    }
  }
  if (chain.length < 2) return none('none');
  // A member standing on a body or a moving deck inherits motion the sum rule does not see.
  const carried = (p: PlayerState) => !!p.supportId && p.grounded && (players.some(q => q.id === p.supportId)
    || world.crates.some(c => c.id === p.supportId) || !!level.platforms.find(s => s.id === p.supportId)?.motion);
  if (chain.some(carried)) return none('carried', chain.length);
  const direction = Number(held.right) - Number(held.left);
  const remotes = chain.filter(p => p !== local);
  const resolved = chain.reduce((sum, p) => sum + p.vx, 0);
  let own: number;
  let reason: ChainInference['reason'];
  const still = chain.every(p => Math.abs(p.vx) <= 1e-6);
  if (still && direction !== 0 && blockedBeyond(chain, direction, level, world)) {
    // Pushing friends into a wall: the wall, not a counter-push, absorbed the displacement.
    own = 0;
    reason = 'wall';
  } else {
    // A still chain under an acknowledged push is read as a stalemate: the service applies an
    // acknowledged input in the acknowledging tick (shared/simulation.ts acknowledge()).
    const share = (resolved - direction * PHYSICS.speed) / remotes.length;
    const snapped = [-PHYSICS.speed, 0, PHYSICS.speed].find(value => Math.abs(share - value) <= INTENT_SNAP);
    if (snapped === undefined) return none('ambiguous', chain.length);
    own = snapped;
    reason = 'shared';
  }
  const deltas = new Map<string, number>();
  for (const remote of remotes) if (Math.abs(remote.vx - own) > EPS) deltas.set(remote.id, own - remote.vx);
  if (!deltas.size) return { world, inference: { members: chain.length, adjusted: 0, own, reason } };
  // Riders (transitively) move with the adjustment so their own inferred direction is unchanged.
  for (let changed = true; changed;) {
    changed = false;
    for (const p of players) {
      if (deltas.has(p.id) || !p.grounded || !p.supportId || !deltas.has(p.supportId)) continue;
      deltas.set(p.id, deltas.get(p.supportId)!);
      changed = true;
    }
  }
  const adjusted: WorldState = { ...world, players: world.players.map(p => deltas.has(p.id) ? { ...p, vx: p.vx + deltas.get(p.id)! } : p) };
  return { world: adjusted, inference: { members: chain.length, adjusted: deltas.size, own, reason } };
}

/** Does the chain's far end in `direction` touch a solid, a closed gate or the world edge? */
function blockedBeyond(chain: PlayerState[], direction: number, level: LevelDef, world: WorldState): boolean {
  const end = chain.reduce((best, p) => (direction > 0 ? p.x > best.x : p.x < best.x) ? p : best, chain[0]);
  if (direction > 0 ? end.x + end.w >= WORLD_W - CHAIN_CONTACT : end.x <= CHAIN_CONTACT) return true;
  const solids: Rect[] = [...getPlatforms(level, world), ...level.gates.filter(gate => !isGateOpen(gate, world))];
  return solids.some(solid => direction > 0 ? touchesRight(end, solid) : touchesRight(solid, end));
}

/**
 * Renderer-owned input journal and predicted trajectory. The socket and its ACKs remain
 * entirely authoritative; this only decides which tick a locally observed input is
 * expected to be applied at and asks the shared engine for those whole ticks.
 *
 * Tick k after the authority samples the newest record whose expected application tick is
 * ≤ k. That tick is ceil(clockTick(sent) + lead): the display clock runs `lead` ticks ahead
 * of the estimated authoritative clock, `lead` being the transport round trip plus the
 * service's step phase, self-calibrated from the courier's own acknowledged transitions
 * (see appliedTick) with a window-minimum fallback. Predictions are memoised per authority
 * and per-tick input signature; a frame samples the two ticks around the render clock.
 */
export class MovementReplay {
  private authority: WorldState | null = null;
  /** The authority with the local push chain's remote velocities replaced by their inferred intent (replay input only). */
  private replayBase: WorldState | null = null;
  private inference: ChainInference = { members: 0, adjusted: 0, own: null, reason: 'none' };
  /** The authority before the current one: the true path for render ticks the clock has not passed yet. */
  private previousAuthority: WorldState | null = null;
  private level: LevelDef | null = null;
  private localId: string | null = null;
  private receivedAt = 0;
  private observed: InputState = { ...EMPTY_INPUT };
  private observedAt = 0;
  private records: InputRecord[] = [];
  /** The input the server is understood to hold as of the authority (newest acknowledged record). */
  private base: InputState = { ...EMPTY_INPUT };
  private suspended = false;
  private cache = new Map<string, WorldState>();
  private leadSamples: { at: number; lead: number }[] = [];
  private calibrations: Calibration[] = [];
  private lead = 0;
  private leadMinimum = 0;
  private leadSource: 'none' | 'acknowledgements' | 'transitions' = 'none';
  private leadAgreement = 0;
  private predictions = 0;
  private cacheHits = 0;
  private lastSample: TrajectorySample | null = null;
  private lastTarget = NaN;
  private lastFrameTime = NaN;
  private lastDt = NaN;
  private resets: Record<string, number> = {};
  /** Diagnostic ring of recent button changes: predicted tick vs. the acknowledging snapshot tick. */
  private transitions: { code: string; at: number; sentAt: number; sequence: number | null; tick: number | null; ackTick: number | null; ackLead: number | null; appliedTick: number | null }[] = [];
  readonly clock = new RenderClock();

  reset(reason: string, input: InputState = this.observed): void {
    this.authority = null;
    this.replayBase = null;
    this.inference = { members: 0, adjusted: 0, own: null, reason: 'none' };
    this.previousAuthority = null;
    this.level = null;
    this.localId = null;
    this.records = [];
    this.cache.clear();
    this.lastSample = null;
    this.observed = { ...input };
    this.base = { ...EMPTY_INPUT };
    this.clock.reset();
    this.resets[reason] = (this.resets[reason] ?? 0) + 1;
  }

  suspend(input: InputState): void {
    if (!this.suspended) this.reset('transport', input);
    this.suspended = true;
  }

  /** Called from passive input listeners (fromEvent), at the start of every rAF and before accepting a snapshot. */
  observe(input: InputState, now: number, fromEvent = false): void {
    if (!Number.isSafeInteger(input.seq)) return;
    const previous = this.observed;
    const changed = !sameButtons(input, previous);
    const sequenceChanged = input.seq !== previous.seq;
    if (!changed && !sequenceChanged) return;
    const previousAt = this.observedAt;
    this.observed = { ...input };
    this.observedAt = now;
    if (this.suspended || !this.authority) return;
    const local = this.authority.players.find(p => p.id === this.localId);
    if (!local || !active(local) || this.authority.complete) { this.records = []; return; }
    if (input.seq < previous.seq) { this.reset('input-rollback', input); return; }
    const stamped = sequenceChanged ? transmissionTime(input.seq, now) : null;
    if (sequenceChanged) {
      // A button can change before the transport stamps its next sequence (touch controls).
      // Bind that observation to the first following transmission; it kept its original time.
      for (const pending of this.records) {
        if (pending.sequence !== null) continue;
        pending.sequence = input.seq;
        pending.sentAt = stamped ?? Math.max(pending.sentAt, previousAt);
      }
    }
    if (changed) {
      // A change observed from the input event itself was transmitted in that same dispatch: its
      // observation time IS the transmission time (the wall-clock stamp only adds ±1 ms noise).
      this.records.push({ input: { ...input }, at: now, sentAt: fromEvent ? now : stamped ?? previousAt,
        sequence: sequenceChanged ? input.seq : null, changed: true, before: { ...previous }, tick: null, measured: false });
      this.transitions.push({ code: code(input), at: now, sentAt: this.records.at(-1)!.sentAt, sequence: this.records.at(-1)!.sequence, tick: null, ackTick: null, ackLead: null, appliedTick: null });
      if (this.transitions.length > 12) this.transitions.shift();
    } else if (!this.records.some(record => record.sequence === input.seq)) {
      // A periodic transmission of unchanged buttons: it becomes a lead sample once acknowledged.
      this.records.push({ input: { ...input }, at: now, sentAt: stamped ?? previousAt, sequence: input.seq, changed: false, before: null, tick: null, measured: false });
    }
    // A bounded journal is independent of the network hook's own bounded send queue.
    if (this.records.length > 128) this.records.splice(0, this.records.length - 128);
  }

  /** Returns true when the authority changed (the renderer folds the visual difference into its offsets). */
  accept(world: WorldState, level: LevelDef, localId: string | null, input: InputState, now: number, snapshots: SnapshotClock): boolean {
    this.observe(input, now);
    const local = world.players.find(p => p.id === localId);
    const previous = this.authority;
    const sameEpoch = previous && previous.epoch === world.epoch && previous.levelIndex === world.levelIndex && this.localId === localId;
    const membershipChanged = previous && (previous.players.some(p => {
      const next = world.players.find(q => q.id === p.id);
      return !next || next.connected !== p.connected || next.entered !== p.entered;
    }) || world.players.some(p => !previous.players.some(q => q.id === p.id)) || previous.crates.length !== world.crates.length);
    const gap = previous ? now - this.receivedAt : 0;
    if (!sameEpoch || this.suspended) {
      this.records = [];
      this.base = { ...EMPTY_INPUT };
      this.clock.reset();
      const reason = this.suspended ? 'transport-resume' : previous ? 'epoch-or-identity' : 'fresh-authority';
      this.resets[reason] = (this.resets[reason] ?? 0) + 1;
    } else if (membershipChanged) {
      this.resets.membership = (this.resets.membership ?? 0) + 1;
    } else if (gap >= MAX_MS) {
      this.resets['snapshot-gap'] = (this.resets['snapshot-gap'] ?? 0) + 1;
    }
    this.suspended = false;
    this.previousAuthority = sameEpoch && !membershipChanged && previous && previous.tick < world.tick ? previous : null;
    this.authority = world;
    this.replayBase = world;
    this.inference = { members: 0, adjusted: 0, own: null, reason: 'none' };
    this.level = level;
    this.localId = localId;
    this.receivedAt = now;
    this.cache.clear();
    if (!local || !active(local) || world.complete) { this.records = []; this.lastSample = null; return true; }
    // LOCAL ACK only. Other couriers' sequence spaces are unrelated.
    const previousLocal = sameEpoch && !membershipChanged ? previous?.players.find(p => p.id === localId) : undefined;
    for (const record of this.records) {
      if (record.measured || record.sequence === null || record.sequence > local.lastInput) continue;
      record.measured = true;
      if (!snapshots.ready) continue;
      const clockTick = snapshots.tickAt(record.sentAt);
      this.leadSamples.push({ at: now, lead: world.tick - clockTick });
      const transition = this.transitions.find(t => t.sequence === record.sequence && t.ackTick === null);
      if (transition) { transition.ackTick = world.tick; transition.ackLead = world.tick - clockTick; }
      const applied = record.changed && previousLocal && previous && localId ? appliedTick(record, previous, world, localId) : null;
      if (applied !== null) {
        this.calibrations.push({ at: now, lo: applied - 1 - clockTick, hi: applied - clockTick, tick: applied });
        if (transition) transition.appliedTick = applied;
        if (this.calibrations.length > CALIBRATION_LIMIT) this.calibrations.shift();
      }
    }
    this.leadSamples = this.leadSamples.filter(sample => now - sample.at <= LEAD_WINDOW_MS);
    this.calibrations = this.calibrations.filter(sample => now - sample.at <= CALIBRATION_WINDOW_MS);
    this.estimateLead();
    const acknowledged = this.records.filter(record => record.sequence !== null && record.sequence <= local.lastInput);
    if (acknowledged.length) this.base = { ...acknowledged.at(-1)!.input };
    this.records = this.records.filter(record => record.sequence === null || record.sequence > local.lastInput);
    // The held input the service applied at this authority decides what the couriers touching
    // the local one were doing; the replay starts from those intents rather than raw push speeds.
    if (localId) {
      const inferred = inferChainIntent(world, level, localId, this.base);
      this.replayBase = inferred.world;
      this.inference = inferred.inference;
    }
    return true;
  }

  /**
   * The lead that makes ceil(clockTick + lead) the tick the service applies an input at.
   * Exact constraints come from the courier's own acknowledged transitions (the authoritative
   * position change across the acknowledging snapshot tells the exact application tick); until
   * one exists, the window minimum of acknowledgement leads minus half a tick is used.
   */
  private estimateLead(): void {
    if (this.calibrations.length) {
      // The value inside the most constraints (robust to one mis-read transition), then the
      // midpoint of those constraints' intersection.
      let best: Calibration[] = [];
      for (const point of this.calibrations.flatMap(c => [c.lo + 1e-6, c.hi])) {
        const satisfied = this.calibrations.filter(c => c.lo < point && point <= c.hi);
        if (satisfied.length > best.length) best = satisfied;
      }
      const maxLo = Math.max(...best.map(c => c.lo));
      const minHi = Math.min(...best.map(c => c.hi));
      // Blend the intersection midpoint (unbiased but set by two noisy edges) with the median of
      // the satisfied constraints' midpoints (steady, unbiased over random input phases), and damp
      // updates so one noisy acknowledgement cannot move the applied tick by itself.
      const midpoints = best.map(c => (c.lo + c.hi) / 2).sort((a, b) => a - b);
      const estimate = ((maxLo + minHi) / 2 + midpoints[Math.floor(midpoints.length / 2)]) / 2;
      this.lead = Math.max(0, this.leadSource === 'transitions' ? this.lead + 0.5 * (estimate - this.lead) : estimate);
      this.leadAgreement = best.length / this.calibrations.length;
      this.leadSource = 'transitions';
      if (this.leadSamples.length) this.leadMinimum = Math.min(...this.leadSamples.map(sample => sample.lead));
      return;
    }
    if (this.leadSamples.length >= 3) {
      this.leadMinimum = Math.min(...this.leadSamples.map(sample => sample.lead));
      this.lead = Math.max(0, this.leadMinimum - LEAD_FALLBACK_MARGIN_TICKS);
      this.leadSource = 'acknowledgements';
    }
  }

  get active(): boolean {
    const local = this.authority?.players.find(p => p.id === this.localId);
    return !!this.authority && !!this.level && !!local && active(local) && !this.authority.complete && !this.suspended;
  }

  /** Advance the local render clock for this frame; returns its tick, or null while inactive. */
  advance(frameTime: number, dtMs: number, snapshots: SnapshotClock): number | null {
    if (!this.active || !snapshots.ready) { this.lastSample = null; return null; }
    const target = snapshots.tickAt(frameTime) + this.lead;
    this.lastTarget = target; this.lastFrameTime = frameTime; this.lastDt = dtMs;
    let tau = this.clock.advance(target, dtMs);
    for (const record of this.records) {
      if (record.tick !== null) continue;
      // The applied tick follows the calibrated target clock, not the slewed display clock (which
      // may trail it briefly after a lead change). An input observed inside this frame counts as
      // sent at the frame's start, so its tick is never later than the tick drawn at the target.
      record.tick = Math.ceil(snapshots.tickAt(Math.min(record.at, frameTime)) + this.lead - EPS);
      // A button change is drawn in the very frame it is observed: if the display clock trails
      // the target (or has not passed the newest authority yet, where the received interval is
      // drawn), it catches up now, masked by the motion change itself (never moves back).
      if (record.changed) tau = this.clock.set(Math.max(target, this.authority!.tick + 0.05));
      const transition = this.transitions.find(t => t.at === record.at && t.tick === null);
      if (transition) { transition.tick = record.tick; transition.sequence ??= record.sequence; }
    }
    return tau;
  }

  private inputForTick(tick: number): InputState {
    let held = this.base;
    for (const record of this.records) {
      if (record.tick === null || record.tick > tick) break;
      held = record.input;
    }
    return held;
  }

  private run(world: WorldState, commands: Command[]): WorldState {
    const local = world.players.find(p => p.id === this.localId)!;
    const safeCommands = commands.map(command => ({ ...command, input: { ...command.input, seq: Math.max(command.input.seq, local.lastInput) } }));
    // Same positions, clock and semantics as the authority; only the push chain's inferred intents differ.
    const start = this.replayBase && this.replayBase.tick === world.tick ? this.replayBase : world;
    let predicted = predictMovementWorld(start, this.level!, this.localId!, safeCommands);
    // A future reset is not ours to invent. Stop at the last drawable physical tick
    // if a speculative fall would put a connected courier outside the world canvas.
    let ticks = Math.round((predicted.elapsed - world.elapsed) * TICK_RATE);
    while (ticks > 0 && predicted.players.some(p => active(p) && (p.y < 0 || p.y + p.h > WORLD_H))) {
      ticks--;
      let seconds = ticks / TICK_RATE;
      const shorter = safeCommands.flatMap(command => {
        const duration = Math.min(seconds, command.duration); seconds -= duration;
        return duration > 0 ? [{ ...command, duration }] : [];
      });
      predicted = predictMovementWorld(start, this.level!, this.localId!, shorter);
    }
    return predicted;
  }

  /** The predicted world exactly `ticks` whole ticks after the authority (memoised). */
  private predict(ticks: number): WorldState {
    const world = this.authority!;
    if (ticks <= 0) return world;
    const inputs: InputState[] = [];
    for (let k = 1; k <= ticks; k++) inputs.push(this.inputForTick(world.tick + k));
    const key = `${ticks}|${inputs.map(code).join('')}`;
    const cached = this.cache.get(key);
    if (cached) { this.cacheHits++; return cached; }
    const commands: Command[] = [];
    for (const input of inputs) {
      const last = commands.at(-1);
      if (last && sameButtons(last.input, input)) last.duration += STEP;
      else commands.push({ input: { ...input }, duration: STEP });
    }
    const predicted = this.run(world, commands);
    this.predictions++;
    this.cache.set(key, predicted);
    if (this.cache.size > CACHE_LIMIT) this.cache.delete(this.cache.keys().next().value!);
    return predicted;
  }

  /** The two whole predicted ticks around a render-clock tick, from the current authority. */
  sample(tau: number, remember = true): TrajectorySample | null {
    const world = this.authority;
    if (!world || !this.active || !Number.isFinite(tau)) return null;
    if (tau < world.tick - EPS) {
      // The render clock has not reached the newest authority yet (a small lead just after a
      // snapshot): the true path there is the received interval itself, never a guess.
      const before = this.previousAuthority;
      const frac = before ? clamp((tau - before.tick) / (world.tick - before.tick), 0, 1) : 1;
      const sample = { lo: before ?? world, hi: world, frac, ticks: 0, tick: tau };
      if (remember) this.lastSample = sample;
      return sample;
    }
    const k = clamp(Math.floor(tau + EPS) - world.tick, 0, MAX_TICKS);
    const hiK = Math.min(k + 1, MAX_TICKS);
    const frac = hiK === k ? 0 : clamp(tau - world.tick - k, 0, 1);
    const sample = { lo: this.predict(k), hi: this.predict(hiK), frac, ticks: hiK, tick: tau };
    if (remember) this.lastSample = sample;
    return sample;
  }

  get leadTicks(): number { return this.lead; }

  private leadSpread(): { count: number; min: number; p10: number; p25: number; p50: number; p75: number; p90: number; max: number; mean: number } | null {
    if (!this.leadSamples.length) return null;
    const sorted = this.leadSamples.map(sample => sample.lead).sort((a, b) => a - b);
    const at = (q: number) => sorted[Math.min(sorted.length - 1, Math.floor(q * sorted.length))];
    return { count: sorted.length, min: sorted[0], p10: at(0.1), p25: at(0.25), p50: at(0.5), p75: at(0.75), p90: at(0.9), max: sorted.at(-1)!, mean: sorted.reduce((a, b) => a + b, 0) / sorted.length };
  }

  diagnostics() {
    const world = this.authority;
    return { method: 'sub-tick-interpolated-input-replay' as const, active: !!this.lastSample,
      horizonMs: this.lastSample && world ? (this.lastSample.hi.tick - world.tick) * TICK_MS : 0, maximumHorizonMs: MAX_MS,
      pendingInputs: this.records.length, commands: this.lastSample ? this.lastSample.ticks : 0,
      acknowledgedSequence: world?.players.find(p => p.id === this.localId)?.lastInput ?? 0,
      predictedElapsed: this.lastSample?.hi.elapsed ?? null,
      leadTicks: this.lead, leadSource: this.leadSource, leadMinimumTicks: this.leadMinimum, leadSamples: this.leadSamples.length, leadCalibrations: this.calibrations.length, leadAgreement: this.leadAgreement,
      leadSpread: this.leadSpread(), transitions: this.transitions.map(t => ({ ...t })), renderTick: this.clock.tick, clockRate: this.clock.rate,
      clockTarget: this.lastTarget, frameTime: this.lastFrameTime, frameDt: this.lastDt,
      clockResyncs: this.clock.resyncs, predictions: this.predictions, cacheHits: this.cacheHits, resets: { ...this.resets },
      chainIntent: { ...this.inference, heldDirection: Number(this.base.right) - Number(this.base.left) } };
  }
}
